import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { useSEO, SITE_URL } from '@/lib/seo';
import { POSTS } from '@/lib/blogPosts';
import { DishImage } from '@/components/DishImage';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';

export default function BlogIndex() {
  useSEO({
    title: 'Indian Recipe Blog & Cooking Guides | Masala Spin',
    description:
      'Long-form Indian cooking guides from Masala Spin — Maharashtrian dinners, Kerala breakfasts, millet cooking, a 21-day meal plan and 50 weeknight dinner ideas.',
    canonical: `${SITE_URL}/blog`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Masala Spin Recipe Blog',
      url: `${SITE_URL}/blog`,
      publisher: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
      blogPost: POSTS.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        description: p.description,
        url: `${SITE_URL}/blog/${p.slug}`,
        datePublished: p.date,
        dateModified: p.dateModified || p.date,
        image: p.cover ? `${SITE_URL}${p.cover}` : undefined,
        author: { '@type': 'Organization', name: 'Masala Spin' },
      })),
    },
  });

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />

      <main className="max-w-5xl mx-auto px-6 py-14">
        <Breadcrumbs items={[{ name: 'Recipe Blog' }]} />

        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-3">
            The Masala Spin Blog
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-4">
            Indian Recipe Guides &amp; Cooking Notes
          </h1>
          <p className="text-base text-[#1C1917]/65 font-['Outfit'] leading-relaxed">
            Each guide below groups recipes that already exist on Masala Spin — by region, by grain or
            by how much time you have — and gives you the approximate time, main ingredients and a
            link to the full method for every dish mentioned. Times and calorie figures are
            estimates from our recipe cards.
          </p>
        </div>

        <ul className="grid md:grid-cols-2 gap-8">
          {POSTS.map((post) => (
            <li key={post.slug}>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group block h-full rounded-3xl overflow-hidden bg-white border border-[#1C1917]/5 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <DishImage
                    src={post.cover}
                    alt={`${post.title} — Masala Spin recipe guide`}
                    fallbackInitial={post.title?.[0]}
                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {post.region && (
                    <span className="absolute top-3 left-3 px-3 py-1 bg-white/85 backdrop-blur rounded-full text-[10px] uppercase tracking-widest font-['Outfit'] font-semibold text-[#F97316]">
                      {post.region}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <p className="flex items-center gap-1.5 text-xs text-[#1C1917]/50 font-['Outfit'] mb-2">
                    <Clock className="w-3 h-3" aria-hidden="true" /> {post.readMinutes} min read
                  </p>
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917] mb-2 leading-tight">
                    {post.title}
                  </h2>
                  <p className="text-sm text-[#1C1917]/65 font-['Outfit'] leading-relaxed mb-4">
                    {post.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-['Outfit'] font-semibold text-[#F97316]">
                    Read article <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <SiteFooter />
    </div>
  );
}
